import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import { Button, Modal, Trans } from '@/components'
import { cn } from '@/utils/style'

import { CatalogModel, ROUTE_TYPE_LABEL_KEYS, RouteModelType } from '../../types'

const COLLAPSED_ROWS = 3

export interface ReviewGroup {
  routeType: RouteModelType
  models: CatalogModel[]
}

interface ReviewModalProps {
  open: boolean
  groups: ReviewGroup[]
  creating: boolean
  /** Editing an existing route: switches the title/hint copy from "create" to "save" */
  isEdit?: boolean
  onClose: () => void
  onConfirm: () => void
}

function ReviewGroupCard({ group }: { group: ReviewGroup }) {
  const { t } = useTranslation('modelRoute')
  const [expanded, setExpanded] = useState(false)

  const visibleModels = expanded ? group.models : group.models.slice(0, COLLAPSED_ROWS)
  const collapsible = group.models.length > COLLAPSED_ROWS

  return (
    // border (not ring): ring is a box-shadow and gets clipped by the modal body's scroll container
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-panel-gray-100">
        <span className="t-sb text-primary">
          {t(ROUTE_TYPE_LABEL_KEYS[group.routeType], group.routeType)}
        </span>
        <span className="t-sb text-highlight">
          {t('create.selectedBadge', '{{count}} selected', { count: group.models.length })}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4 px-4 py-2 t-xs text-secondary border-b border-neutral-100">
        <div>
          <Trans ns="modelRoute" i18nKey="create.headerModel">
            Model
          </Trans>
        </div>
        <div>
          <Trans ns="modelRoute" i18nKey="create.headerInputPrice">
            Input Price -1M
          </Trans>
        </div>
        <div>
          <Trans ns="modelRoute" i18nKey="create.headerOutputPrice">
            Output Price -1M
          </Trans>
        </div>
      </div>

      {visibleModels.map((model) => (
        <div
          key={model.id}
          className="grid grid-cols-3 gap-4 px-4 py-3 border-b border-neutral-100 last:border-b-0 t-sm"
        >
          <div className="t-sb text-primary">{model.model}</div>
          <div>
            <span className="t-sb text-primary">{model.inputPrice}</span>{' '}
            <span className="text-secondary">
              <Trans ns="modelRoute" i18nKey="table.credits">
                Credits
              </Trans>
            </span>
          </div>
          <div>
            <span className="t-sb text-primary">{model.outputPrice}</span>{' '}
            <span className="text-secondary">
              <Trans ns="modelRoute" i18nKey="table.credits">
                Credits
              </Trans>
            </span>
          </div>
        </div>
      ))}

      {collapsible && (
        <div
          className="flex items-center justify-center gap-1 py-3 t-sb text-highlight cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? t('create.collapseLists', 'Collapse lists')
            : t('create.viewAllModels', 'View all {{count}} models', {
                count: group.models.length,
              })}
          <ArrowDownIcon
            className={cn('w-4 h-4 transition-transform', { 'rotate-180': expanded })}
          />
        </div>
      )}
    </div>
  )
}

export function ReviewModal({
  open,
  groups,
  creating,
  isEdit = false,
  onClose,
  onConfirm,
}: ReviewModalProps) {
  const totalCount = groups.reduce((sum, group) => sum + group.models.length, 0)

  return (
    <Modal open={open} onClose={onClose} className="w-[640px]">
      <Modal.Header>
        {isEdit ? (
          <Trans ns="modelRoute" i18nKey="create.reviewEditTitle">
            Save Model Route
          </Trans>
        ) : (
          <Trans ns="modelRoute" i18nKey="create.reviewTitle">
            Create New Model Route
          </Trans>
        )}
      </Modal.Header>
      <Modal.Body scrollable>
        <div className="flex flex-col gap-4">
          <div className="t-sm text-secondary">
            {isEdit ? (
              <Trans
                ns="modelRoute"
                i18nKey="create.reviewEditHint"
                values={{ count: totalCount }}
                components={[<span key="0" className="text-highlight" />]}
              >
                {
                  'Review <0>{{count}} selected models</0> across all route types before saving changes to this route.'
                }
              </Trans>
            ) : (
              <Trans
                ns="modelRoute"
                i18nKey="create.reviewHint"
                values={{ count: totalCount }}
                components={[<span key="0" className="text-highlight" />]}
              >
                {
                  'Review <0>{{count}} selected models</0> across all route types before creating this route.'
                }
              </Trans>
            )}
          </div>

          {groups.map((group) => (
            <ReviewGroupCard key={group.routeType} group={group} />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" className="min-w-[88px]" onClick={onClose} disabled={creating}>
          <Trans ns="modelRoute" i18nKey="create.cancel">
            Cancel
          </Trans>
        </Button>
        {/* Design shows "Recharge" here — assumed to be the insufficient-credits
            variant; confirm creates the route directly (Recharge variant pending PM) */}
        <Button type="secondary" className="min-w-[88px]" onClick={onConfirm} disabled={creating}>
          <Trans ns="modelRoute" i18nKey="create.confirm">
            Confirm
          </Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
